import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { listPosSales, PosSaleListRow } from '../../../services/posApi';
import styles from './CounterSalesHistory.module.css';

const PAGE_SIZE = 25;

function money(n: number): string {
  return `$${(Number.isFinite(n) ? n : 0).toFixed(2)}`;
}

const CounterSalesHistory: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const [rows, setRows] = useState<PosSaleListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listPosSales({
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setRows(res.data);
      setTotal(res.total);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || 'Failed to load counter sales');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, page]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Counter Sales</h2>
        <div className={styles.filters}>
          <label>
            From
            <input type="date" value={fromDate} onChange={(e) => { setPage(0); setFromDate(e.target.value); }} />
          </label>
          <label>
            To
            <input type="date" value={toDate} onChange={(e) => { setPage(0); setToDate(e.target.value); }} />
          </label>
        </div>
      </div>

      {error && <div className={styles.errorBanner}>{error}</div>}

      <div className={styles.tableCard}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Date / Time</th>
              <th>Buyer</th>
              <th>Mobile</th>
              <th className={styles.num}>Items</th>
              <th className={styles.num}>Total</th>
              <th>Payment</th>
              <th>Invoice #</th>
              {isAdmin && <th>Sold By</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={isAdmin ? 8 : 7} className={styles.center}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={isAdmin ? 8 : 7} className={styles.center}>No counter sales found.</td></tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id}>
                  <td>{new Date(r.created_at).toLocaleString()}</td>
                  <td>{r.buyer_name || '—'}</td>
                  <td>{r.buyer_mobile || '—'}</td>
                  <td className={styles.num}>{r.item_count}</td>
                  <td className={styles.num}>{money(r.total_amount)}</td>
                  <td>{r.payment_method === 'credit_card' ? 'Card' : r.payment_method === 'cash' ? 'Cash' : (r.payment_method || '—')}</td>
                  <td className={styles.mono}>{r.invoice_number || '—'}</td>
                  {isAdmin && <td>{r.sold_by || '—'}</td>}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.pager}>
        <span>{total} sale{total === 1 ? '' : 's'}</span>
        <div className={styles.pagerBtns}>
          <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Prev</button>
          <span>Page {page + 1} of {totalPages}</span>
          <button disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      </div>
    </div>
  );
};

export const CounterSalesHistoryPage: React.FC = () => <CounterSalesHistory />;
export default CounterSalesHistory;
